'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { AffiliateService } from '@/services/affiliate';
import { AffiliateContext, AffiliateFamily, AffiliateProduct } from '@/types/affiliate';
import { dedupeProducts, isAmazonStore, resolveAffiliateGender } from '@/utils/affiliate';
import { useUserProfile } from '@/hooks/use-user';
import { useAuth } from '@/hooks/use-auth';
import { useIsSpain } from '@/hooks/use-is-spain';
import { Dictionary } from '@/dictionaries';
import ProductCard from './ProductCard';
import { AFFILIATE_CARD_MIN_WIDTH_PX } from './amazonBadge.constants';
import { buildProductsHref } from './products.constants';

const affiliateService = AffiliateService.getInstance();

// Pedimos productos de más para poder descartar duplicados y variantes de talla
// del mismo modelo, y aun así completar el número a mostrar.
const OVERFETCH_BUFFER = 8;

interface AffiliateProductsSectionProps {
  lang: string;
  dict: Dictionary;
  sportId?: number | string;
  /** Subdeporte/categoría para mayor precisión. Cae a sportId si no hay inventario. */
  subSportId?: number | string;
  family?: AffiliateFamily;
  /** Número de productos a mostrar (def. 4). */
  n?: number;
  title?: string;
  /** Página de origen, se envía como `ctx` (consulta y clickUrl) para tracking. */
  ctx: AffiliateContext;
}

export default function AffiliateProductsSection({
  lang,
  dict,
  sportId,
  subSportId,
  family,
  n = 4,
  title,
  ctx,
}: AffiliateProductsSectionProps) {
  const { userProfile, loading: loadingUser } = useUserProfile();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const isSpain = useIsSpain();
  const [products, setProducts] = useState<AffiliateProduct[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading || loadingUser) return;

    const abortController = new AbortController();
    const gender = isAuthenticated
      ? resolveAffiliateGender(userProfile?.gender, userProfile?.birthDate)
      : undefined;

    const fetchProducts = async () => {
      setLoading(true);
      const { data, error } = await affiliateService.getBanners({
        sportId,
        subSportId,
        family,
        gender,
        ctx,
        n: n + OVERFETCH_BUFFER,
        signal: abortController.signal,
      });

      if (abortController.signal.aborted) return;

      if (error || !data) {
        setProducts([]);
      } else {
        setProducts(dedupeProducts(data).slice(0, n));
      }
      setLoading(false);
    };

    fetchProducts();

    return () => abortController.abort();
  }, [isAuthenticated, userProfile?.gender, userProfile?.birthDate, authLoading, loadingUser, sportId, subSportId, family, n, ctx]);

  // No pintamos el bloque mientras carga, ni si no hay inventario (requisito del doc).
  if (loading || products.length === 0) {
    return null;
  }

  const heading = title || dict.affiliate.section_title;

  // Si el bloque incluye algún producto de Amazon, las tarjetas necesitan un
  // ancho mínimo que permita encajar el cartel "disponible en amazon" (≥140px).
  // En ese caso usamos un grid auto-fit con ancho mínimo de tarjeta; si no, el
  // layout compacto habitual (2 / 4 columnas).
  const hasAmazon = products.some((product) => isAmazonStore(product.store));

  return (
    <section className="py-4 sm:py-8">
      <div className="mb-4 flex items-baseline gap-2">
        <h2 className="text-2xl font-bold">{heading}</h2>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
          {dict.affiliate.advertising}
        </span>
        {/* Aquí solo caben 4 productos: el listado completo está en /products,
            con el deporte de este bloque ya preseleccionado. */}
        <Link
          href={buildProductsHref(lang, sportId)}
          className="ml-auto shrink-0 whitespace-nowrap text-[13px] font-semibold text-primary hover:underline"
        >
          {dict.affiliate.see_all} →
        </Link>
      </div>

      <div
        className={
          hasAmazon
            ? 'grid gap-3.5 sm:gap-4'
            : 'grid grid-cols-2 gap-3.5 sm:gap-4 lg:grid-cols-4'
        }
        style={
          hasAmazon
            ? { gridTemplateColumns: `repeat(auto-fit, minmax(${AFFILIATE_CARD_MIN_WIDTH_PX}px, 1fr))` }
            : undefined
        }
      >
        {products.map((product) => (
          <ProductCard
            key={product.productId}
            product={product}
            lang={lang}
            seeProductLabel={dict.affiliate.see_product}
            amazonAvailableLabel={dict.affiliate.available_at_amazon}
            isSpain={isSpain}
            ctx={ctx}
          />
        ))}
      </div>

      <p className="mt-3 text-[11px] leading-snug text-muted-foreground">
        {dict.affiliate.disclosure}
      </p>
    </section>
  );
}
